import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { BACKEND_URL } from '../../config';

/**
 * AdminProtectedRoute - Platform Admin route protection
 * 
 * Checks for admin_token in localStorage and validates it against the backend.
 * Redirects to /admin/login if not authenticated as platform admin.
 */
const AdminProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAdminAuth = async () => {
      const adminToken = localStorage.getItem('admin_token');
      
      if (!adminToken) {
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      try {
        // Validate the admin token with the backend
        const response = await fetch(`${BACKEND_URL}/api/admin/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          // Token is invalid, remove it
          localStorage.removeItem('admin_token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Admin auth check failed:', error);
        setIsAuthenticated(false);
      }
      
      setIsChecking(false);
    };

    checkAdminAuth();
  }, []);

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#6366F1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#A0AEC0]">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to admin login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

export default AdminProtectedRoute;
